import { queryClient } from '@/lib/query/query-client';
import { useAuthStore } from '@/store/auth-store';
import { sessionRevision, sessionScope } from '@/features/auth/session-scope';

export interface AshleyInteraction { conversationId: string; owner: string; revision: number; generation: number }
const generations = new Map<string, number>();
const signatures = new Map<string, string>();
const listeners = new Set<(conversationId: string) => void>();
export const captureAshleyInteraction = (conversationId: string): AshleyInteraction => ({ conversationId, owner: sessionScope(useAuthStore.getState()), revision: sessionRevision(), generation: generations.get(conversationId) ?? 0 });
export function currentAshleyInteraction(value: AshleyInteraction): boolean {
  return value.owner === sessionScope(useAuthStore.getState()) && value.revision === sessionRevision() && value.generation === (generations.get(value.conversationId) ?? 0);
}
export function invalidateAshleyInteraction(conversationId: string): void {
  generations.set(conversationId, (generations.get(conversationId) ?? 0) + 1);
  for (const listener of listeners) listener(conversationId);
}
export function onAshleyInteraction(listener: (conversationId: string) => void): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function observeAshleyInteraction(conversationId: string, messageId?: string, changed = false): void {
  const rows = queryClient.getQueryData<{ id?: string }[]>(['messages', conversationId]);
  if (!changed && messageId && Array.isArray(rows) && rows.some(row => row.id === messageId)) return;
  invalidateAshleyInteraction(conversationId);
}
function messageSignature(value: unknown): string {
  if (!Array.isArray(value)) return 'unavailable';
  return JSON.stringify(value.map(raw => {
    const m = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
    return [m.id, m.conversation_id, m.direction, m.created_at, m.content, m.message_type, m.is_deleted, m.media_caption,
      m.audio_transcript, m.audio_transcript_status, m.media_description, m.media_description_status, m.media_status, m.reply_to_id, m.reply_to];
  }));
}
// A native read can reveal an edit/transcript/quoted edit missed by realtime.
// No model response or timestamp is a substitute for this local generation.
queryClient.getQueryCache().subscribe(event => {
  const key = event.query.queryKey;
  if (key[0] !== 'messages' || typeof key[1] !== 'string') return;
  const signature = event.type === 'removed' ? 'unavailable' : messageSignature(event.query.state.data);
  if (signature !== signatures.get(key[1])) {
    signatures.set(key[1], signature);
    invalidateAshleyInteraction(key[1]);
  }
});
useAuthStore.subscribe((next, previous) => {
  if (sessionScope(next) !== sessionScope(previous)) { generations.clear(); signatures.clear(); }
});
